export type Team = 'blue' | 'red';

export type PlayerRole = 'player' | 'spectator';

export type Phase = 'lobby' | 'psychicTurn' | 'guessing' | 'reveal' | 'gameOver';

/** Position on the spectrum dial (0-100). */
export type DialPosition = number;

export interface Player {
  id: string;
  name: string;
  team: Team | null;
  isConnected: boolean;
  isReady: boolean;
  role: PlayerRole;
}

export type RoomState = 'lobby' | 'playing' | 'finished';

export interface Room {
  id: string;
  code: string;
  players: Player[];
  state: RoomState;
}

export interface Spectrum {
  id: string;
  left: string;
  right: string;
  /** Whether this spectrum is built-in or player-submitted. */
  source?: 'builtin' | 'submitted';
}

/**
 * Extended spectrum entry for player-submitted spectra.
 * Tracks the submitter, vote counts, and which players have voted.
 */
export interface SubmittedSpectrumEntry {
  id: string;
  left: string;
  right: string;
  /** ID of the player who submitted this spectrum. */
  submitterId: string;
  /** Number of upvotes from other players. */
  upvotes: number;
  /** Number of downvotes from other players. */
  downvotes: number;
  /** IDs of players who have voted on this spectrum (prevents double-voting). */
  voterIds: string[];
}

/**
 * Predefined list of spectrums used in the game.
 * Each spectrum has two opposing concepts on a dial.
 * Curated from the original Bangos board game and custom designs.
 */
/** Maximum length of a clue text (FR-9: single word or short phrase). */
export const CLUE_MAX_LENGTH = 50;

/** Maximum length of a submitted spectrum label. */
export const SUBMITTED_LABEL_MAX_LENGTH = 50;

/** Maximum number of custom spectra allowed per game. */
export const MAX_CUSTOM_SPECTRA_PER_GAME = 10;

/** Probability weight (0-1) for picking a submitted spectrum over a built-in one. */
export const CUSTOM_SPECTRUM_WEIGHT = 0.3;

export const SPECTRA: Spectrum[] = [
  { id: 'hot-cold', left: 'Hot', right: 'Cold' },
  { id: 'big-small', left: 'Big', right: 'Small' },
  { id: 'loud-quiet', left: 'Loud', right: 'Quiet' },
  { id: 'fast-slow', left: 'Fast', right: 'Slow' },
  { id: 'heavy-light', left: 'Heavy', right: 'Light' },
  { id: 'bright-dim', left: 'Bright', right: 'Dim' },
  { id: 'wet-dry', left: 'Wet', right: 'Dry' },
  { id: 'hard-soft', left: 'Hard', right: 'Soft' },
  { id: 'sharp-dull', left: 'Sharp', right: 'Dull' },
  { id: 'sweet-sour', left: 'Sweet', right: 'Sour' },
  { id: 'rough-smooth', left: 'Rough', right: 'Smooth' },
  { id: 'deep-shallow', left: 'Deep', right: 'Shallow' },
  { id: 'wide-narrow', left: 'Wide', right: 'Narrow' },
  { id: 'thick-thin', left: 'Thick', right: 'Thin' },
  { id: 'full-empty', left: 'Full', right: 'Empty' },
  { id: 'old-young', left: 'Old', right: 'Young' },
  { id: 'rich-poor', left: 'Rich', right: 'Poor' },
  { id: 'happy-sad', left: 'Happy', right: 'Sad' },
  { id: 'calm-excited', left: 'Calm', right: 'Excited' },
  { id: 'simple-complex', left: 'Simple', right: 'Complex' },
  { id: 'useless-essential', left: 'Useless', right: 'Essential' },
  { id: 'dangerous-safe', left: 'Dangerous', right: 'Safe' },
  { id: 'cheap-expensive', left: 'Cheap', right: 'Expensive' },
  { id: 'weak-strong', left: 'Weak', right: 'Strong' },
  { id: 'ugly-beautiful', left: 'Ugly', right: 'Beautiful' },
  { id: 'stupid-smart', left: 'Stupid', right: 'Smart' },
  { id: 'boring-interesting', left: 'Boring', right: 'Interesting' },
  { id: 'fake-real', left: 'Fake', right: 'Real' },
  { id: 'common-rare', left: 'Common', right: 'Rare' },
  { id: 'bad-good', left: 'Bad', right: 'Good' },
  { id: 'easy-difficult', left: 'Easy', right: 'Difficult' },
  { id: 'soft-loud', left: 'Soft', right: 'Loud' },
  { id: 'dark-light', left: 'Dark', right: 'Light' },
  { id: 'low-high', left: 'Low', right: 'High' },
  { id: 'short-tall', left: 'Short', right: 'Tall' },
  { id: 'stale-fresh', left: 'Stale', right: 'Fresh' },
  { id: 'vague-precise', left: 'Vague', right: 'Precise' },
  { id: 'passionate-indifferent', left: 'Passionate', right: 'Indifferent' },
  { id: 'bitter-savory', left: 'Bitter', right: 'Savory' },
  { id: 'salty-spicy', left: 'Salty', right: 'Spicy' },
  { id: 'cold-warm', left: 'Cold', right: 'Warm' },
  { id: 'dull-shiny', left: 'Dull', right: 'Shiny' },
  { id: 'fragile-durable', left: 'Fragile', right: 'Durable' },
  { id: 'rigid-flexible', left: 'Rigid', right: 'Flexible' },
  { id: 'chaotic-orderly', left: 'Chaotic', right: 'Orderly' },
  { id: 'messy-tidy', left: 'Messy', right: 'Tidy' },
  { id: 'noisy-peaceful', left: 'Noisy', right: 'Peaceful' },
  { id: 'busy-slow', left: 'Busy', right: 'Slow' },
  { id: 'risky-certain', left: 'Risky', right: 'Certain' },
  { id: 'unfair-just', left: 'Unfair', right: 'Just' },
  { id: 'cruel-kind', left: 'Cruel', right: 'Kind' },
  { id: 'selfish-generous', left: 'Selfish', right: 'Generous' },
  { id: 'cowardly-brave', left: 'Cowardly', right: 'Brave' },
  { id: 'lazy-ambitious', left: 'Lazy', right: 'Ambitious' },
  { id: 'shy-outgoing', left: 'Shy', right: 'Outgoing' },
  { id: 'gloomy-cheerful', left: 'Gloomy', right: 'Cheerful' },
  { id: 'serious-silly', left: 'Serious', right: 'Silly' },
  { id: 'tense-relaxed', left: 'Tense', right: 'Relaxed' },
  { id: 'urban-rural', left: 'Urban', right: 'Rural' },
  { id: 'modern-ancient', left: 'Modern', right: 'Ancient' },
];

export interface Clue {
  id: string;
  playerId: string;
  text: string;
  /** Position on the spectrum dial (0-100) that the clue hints toward. */
  position: DialPosition;
}

export interface Guess {
  id: string;
  playerId: string;
  /** The guessed position on the spectrum dial (0-100). */
  position: DialPosition;
}

export interface Score {
  /** Points earned this round (0, 1, 2, 3, or 4 based on distance). */
  points: number;
  /** Absolute distance between guess and target (0-100). */
  distance: number;
}

export interface Round {
  /** The spectrum being used this round. */
  spectrum: Spectrum;
  /** ID of the player who is the psychic for this round. */
  psychicId: string;
  /** The psychic's secret target position on the dial (0-100). */
  targetPosition?: DialPosition;
  /** Clues given by the psychic for their team. */
  clues: Clue[];
  /** Guesses submitted by the guessing team. */
  guesses: Guess[];
  /** Score result for this round, if available. */
  score: Score | null;
}

export interface GameState {
  roomId: string;
  roomCode: string;
  round: number;
  phase: Phase;
  activeTeam: Team | null;
  currentRound: Round | null;
  /** Completed rounds stored for end-of-game recap (clues, guesses, score, etc.). */
  roundHistory: Round[];
  /** Player-submitted spectra for this game session. */
  submittedSpectra: Spectrum[];
  blueScore: number;
  redScore: number;
  totalRounds: number;
  creatorId: string;
  teams: {
    blue: string[];
    red: string[];
  };
}

export const SCORING_BANDS = [
  { maxDistance: 3, points: 4 },
  { maxDistance: 8, points: 3 },
  { maxDistance: 15, points: 2 },
  { maxDistance: 100, points: 1 },
] as const;

/**
 * Calculate score based on the absolute distance between a guess and the target.
 * Distance is expected to be in the range 0-100.
 * Returns points (0, 1, 2, 3, or 4) based on scoring bands.
 */
export function calculateScore(distance: number): number {
  for (const band of SCORING_BANDS) {
    if (distance <= band.maxDistance) {
      return band.points;
    }
  }
  return 0;
}

/**
 * Create a Score object from the absolute distance between guess and target.
 */
export function createScore(distance: number): Score {
  return {
    points: calculateScore(distance),
    distance,
  };
}

// ---- State diffing for performance optimization ----

/**
 * A partial GameState containing only the fields that changed.
 * Used by `game_state_diff` events to reduce broadcast payload size.
 */
export interface GameStateDiff {
  roomId: string;
  roomCode: string;
  playerId?: string;
  round?: number;
  phase?: Phase;
  activeTeam?: Team | null;
  currentRound?: Partial<Round> | null;
  blueScore?: number;
  redScore?: number;
  totalRounds?: number;
  teams?: { blue: string[]; red: string[] };
}

/**
 * Compute a diff between two GameState snapshots.
 * Returns a GameStateDiff containing only the fields that changed.
 * If the GameState is complete (no previous state), returns all fields as a diff.
 */
export function diffGameState(
  current: GameState,
  previous?: GameState | null,
): GameStateDiff {
  const diff: GameStateDiff = {
    roomId: current.roomId,
    roomCode: current.roomCode,
  };

  if (!previous) {
    // No previous state means we send everything
    diff.round = current.round;
    diff.phase = current.phase;
    diff.activeTeam = current.activeTeam;
    diff.blueScore = current.blueScore;
    diff.redScore = current.redScore;
    diff.totalRounds = current.totalRounds;
    diff.teams = current.teams;
    if (current.currentRound) {
      diff.currentRound = { ...current.currentRound };
    } else {
      diff.currentRound = null;
    }
    return diff;
  }

  if (current.round !== previous.round) diff.round = current.round;
  if (current.phase !== previous.phase) diff.phase = current.phase;
  if (current.activeTeam !== previous.activeTeam) diff.activeTeam = current.activeTeam;
  if (current.blueScore !== previous.blueScore) diff.blueScore = current.blueScore;
  if (current.redScore !== previous.redScore) diff.redScore = current.redScore;
  if (current.totalRounds !== previous.totalRounds) diff.totalRounds = current.totalRounds;

  // Check teams (reference equality or deep equality would be expensive; we check if they're different)
  if (current.teams.blue.length !== previous.teams.blue.length ||
      current.teams.red.length !== previous.teams.red.length ||
      current.teams.blue.some((id, i) => id !== previous.teams.blue[i]) ||
      current.teams.red.some((id, i) => id !== previous.teams.red[i])) {
    diff.teams = current.teams;
  }

  // For currentRound, we only track whether it changed (reference/identity)
  // For a full diff we'd drill into clues/guesses, but this is sufficient
  if (current.currentRound !== previous.currentRound) {
    if (current.currentRound) {
      diff.currentRound = { ...current.currentRound };
    } else {
      diff.currentRound = null;
    }
  } else {
    // Same round object — check specific scalar fields that may have changed
    const prev = previous.currentRound;
    const curr = current.currentRound;
    if (prev && curr) {
      // Only send partial round updates if they changed
      const roundDiff: Partial<Round> = {};
      let roundChanged = false;
      if (curr.psychicId !== prev.psychicId) { roundDiff.psychicId = curr.psychicId; roundChanged = true; }
      if (curr.clues.length !== prev.clues.length) { roundDiff.clues = curr.clues; roundChanged = true; }
      if (curr.guesses.length !== prev.guesses.length) { roundDiff.guesses = curr.guesses; roundChanged = true; }
      if (JSON.stringify(curr.spectrum) !== JSON.stringify(prev.spectrum)) { roundDiff.spectrum = curr.spectrum; roundChanged = true; }
      // targetPosition is sanitized per-player so we don't diff it here
      if (roundChanged) diff.currentRound = roundDiff;
    }
  }

  return diff;
}

/**
 * Apply a GameStateDiff on top of an existing GameState.
 * Returns a new GameState with the diff fields merged.
 */
export function applyGameStateDiff(
  baseState: GameState,
  diff: GameStateDiff,
): GameState {
  return {
    ...baseState,
    ...(diff.round !== undefined ? { round: diff.round } : {}),
    ...(diff.phase !== undefined ? { phase: diff.phase } : {}),
    ...(diff.activeTeam !== undefined ? { activeTeam: diff.activeTeam } : {}),
    ...(diff.blueScore !== undefined ? { blueScore: diff.blueScore } : {}),
    ...(diff.redScore !== undefined ? { redScore: diff.redScore } : {}),
    ...(diff.totalRounds !== undefined ? { totalRounds: diff.totalRounds } : {}),
    ...(diff.teams !== undefined ? { teams: diff.teams } : {}),
    ...(diff.currentRound !== undefined
      ? {
          currentRound: diff.currentRound === null
            ? null
            : { ...(baseState.currentRound || {}), ...diff.currentRound } as Round,
        }
      : {}),
  };
}

export type {
  MessageType,
  MessageEnvelope,
  CreateRoomPayload,
  RoomCreatedPayload,
  ConnectPayload,
  DisconnectPayload,
  JoinRoomPayload,
  LeaveRoomPayload,
  RoomJoinedPayload,
  PlayerJoinedPayload,
  PlayerLeftPayload,
  PlayerReadyPayload,
  GameStartedPayload,
  TeamAssignedPayload,
  PsychicAssignedPayload,
  SubmitCluePayload,
  ClueSubmittedPayload,
  SubmitGuessPayload,
  GuessSubmittedPayload,
  RoundRevealedPayload,
  ScoredPayload,
  PhaseChangedPayload,
  RoundStartedPayload,
  GameOverPayload,
  PlayAgainPayload,
  RematchStartedPayload,
  SubmitSpectrumPayload,
  SpectrumSubmittedPayload,
  ReconnectPayload,
  GameStateSyncPayload,
  GameStateDiffPayload,
  PlayerReconnectedPayload,
  RoomStatePayload,
  ErrorPayload,
  ReadyForNextRoundPayload,
  ServerEvents,
  ClientEvents,
} from './envelopes.js';
